const INITIALS_COLORS = [
	"rgba(163, 156, 107, 0.6)", // olive/gold
	"rgba(160, 160, 165, 0.5)", // silver/gray
	"rgba(106, 135, 161, 0.5)", // slate blue
	"rgba(130, 155, 130, 0.5)", // sage green
	"rgba(168, 130, 140, 0.5)", // dusty rose
	"rgba(155, 140, 125, 0.5)" // warm taupe
];

const SUFFIXES = new Set([
	"jr",
	"jr.",
	"sr",
	"sr.",
	"ii",
	"iii",
	"iv",
	"v",
	"esq",
	"esq."
]);

export function getInitials(name: string): string {
	const parts = name
		.trim()
		.split(/\s+/)
		.filter((p) => !SUFFIXES.has(p.toLowerCase()));
	if (parts.length <= 1) return "";
	return parts.map((p) => p.charAt(0).toUpperCase()).join("");
}

export function getInitialsColor(name: string): string {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = (hash << 5) - hash + name.charCodeAt(i);
		hash |= 0;
	}
	const index = Math.abs(hash) % INITIALS_COLORS.length;
	return INITIALS_COLORS[index];
}

const CLOSING_REGEX =
	/^(Sincerely|Best regards|Best wishes|Warm regards|Respectfully|Kind regards|Thank you|Yours truly|With appreciation|Cordially),?\s*$/im;

export interface CoverLetterParts {
	body: string;
	closingLine: string;
	typedName: string;
}

export function parseCoverLetterParts(text: string): CoverLetterParts {
	const lines = text.split("\n");

	let closingIndex = -1;
	for (let i = lines.length - 1; i >= 0; i--) {
		if (CLOSING_REGEX.test(lines[i].trim())) {
			closingIndex = i;
			break;
		}
	}

	if (closingIndex >= 0) {
		const body = lines.slice(0, closingIndex).join("\n").trimEnd();
		const closingLine = lines[closingIndex].trim();

		// Typed name is the last non-empty line after the closing
		const remaining = lines.slice(closingIndex + 1);
		const nameLine = remaining.filter((l) => l.trim().length > 0).pop();
		const typedName = nameLine?.trim() ?? "";

		return { body, closingLine, typedName };
	}

	// Fallback: treat last non-empty line as typed name, no closing
	const nonEmpty = lines.filter((l) => l.trim().length > 0);
	const typedName =
		nonEmpty.length > 0 ? nonEmpty[nonEmpty.length - 1].trim() : "";
	const body = nonEmpty.slice(0, -1).join("\n").trimEnd();
	return { body, closingLine: "", typedName };
}

export function buildFilename(company: string): string {
	const sanitized = company.replace(/[^a-zA-Z0-9\s]/g, "").replace(/\s+/g, "_");
	const date = new Date().toISOString().slice(0, 10);
	return `CoverLetter_${sanitized}_${date}.pdf`;
}

export function formatLetterDate(date: Date): string {
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric"
	});
}
